import LocalStorageType from '../constants/LocalStorageType';

/**
 * Acesso a módulo do usuário logado, fora do menu.
 *
 * Repete a regra do SideBar (empresa precisa ter contratado o módulo e, para quem
 * não é administrador, o grupo precisa liberá-lo) para as telas que também precisam
 * decidir o que mostrar. Esconder o item de menu não basta: quem digita a URL chega
 * na tela do mesmo jeito.
 *
 * É uma checagem de interface, não de segurança — o backend valida de novo em
 * ModuleAccessGuard, que é quem de fato protege o dado.
 */
export const hasModuleAccess = (module) => {
    let user = null;

    try {
        user = JSON.parse(localStorage.getItem(LocalStorageType.USER_DETAILS));
    } catch {
        return false;
    }

    if (!user) {
        return false;
    }

    // O super admin opera a plataforma e não assina plano: nenhuma restrição de módulo
    // vale para ele, nem mesmo a da empresa que estiver acessando no momento.
    if (user.superAdmin === true) {
        return true;
    }

    // A lista vem das permissões do plano; lista vazia significa que nenhum módulo
    // foi contratado e não deve liberar acesso por omissão.
    const companyModules = user.companyPermissions;
    const companyAllows = !Array.isArray(companyModules) || companyModules.includes(module);

    if (!companyAllows) {
        return false;
    }

    if (user.admin === true) {
        return true;
    }

    return Array.isArray(user.permissions) && user.permissions.includes(module);
};

/**
 * Acesso a um item de menu (submenu) específico do usuário logado.
 *
 * Mesma regra do SideBar (canDisplaySubMenuItem): o módulo precisa estar liberado
 * e, para quem não é administrador, o menuKey precisa constar na allow-list do
 * grupo. Serve para as telas que agrupam atalhos de outros módulos, como a Central
 * de Relatórios, e não podem virar um caminho alternativo para o que o menu esconde.
 */
export const hasMenuAccess = (module, menuKey) => {
    if (!hasModuleAccess(module)) {
        return false;
    }

    let user = null;

    try {
        user = JSON.parse(localStorage.getItem(LocalStorageType.USER_DETAILS));
    } catch {
        return false;
    }

    if (user?.admin === true || user?.superAdmin === true) {
        return true;
    }

    // Sessões anteriores a essa funcionalidade ainda não têm menuPermissions no
    // localStorage: não restringe até o próximo login, igual ao SideBar.
    if (user?.menuPermissions === undefined) {
        return true;
    }

    return Array.isArray(user.menuPermissions)
        && user.menuPermissions.some(item => item.permission === module && item.menuKey === menuKey);
};

export default hasModuleAccess;
